import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import { formatDateRangeForMonth } from "@/utils/globalUtils";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { Button, DatePicker, Form, Select, Spin } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import BillingReportBody from "./BillingReportBody";
import BillingReportPDF from "./BillingReportPDF";
import { flare } from "@flareapp/js";

const initReportValues = {};

function getReportType(reportName) {
  if (!reportName) return false;
  const reportType1 = ["tariff a", "tariff b", "tariff d", "tariff c1", "tariff e1", "tariff h", "tariff h1"];
  const reportType2 = ["tariff c2", "tariff e2", "tariff e3", "tariff h2"];

  const normalizedName = reportName.toLowerCase();
  const regexType1 = reportType1.map((type) => new RegExp(`\\b${type}\\b`, "i"));
  const regexType2 = reportType2.map((type) => new RegExp(`\\b${type}\\b`, "i"));

  if (regexType1.some((regex) => regex.test(normalizedName))) {
    return "type2";
  }
  if (regexType2.some((regex) => regex.test(normalizedName))) {
    return "type1";
  }

  return false;
}

const BillingReport = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // const { userDetails } = useSelector((state) => state.user || {});
  // const firstBusiness = userDetails?.business?.[0] || {};

  // states
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [reportValues, setReportValues] = useState(initReportValues);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessList, setBusinessList] = useState([]);

  const [siteLoading, setSiteLoading] = useState(false);
  const [siteList, setSiteList] = useState([]);

  const [deviceList, setDeviceList] = useState([]);
  const [isDeviceLoading, setIsDeviceLoading] = useState(false);

  const getBusiness = async () => {
    setBusinessLoading(true);
    try {
      const data = await api.get(endpoint.business);
      setBusinessList(data?.data);
      if (data?.data && data?.data?.length === 1) {
        const bID = data?.data[0]?._id;
        form.setFieldValue("business", bID);
        getSiteLocations(bID);
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("business list", error);
    }
    setBusinessLoading(false);
  };

  const getSiteLocations = async (business) => {
    setSiteLoading(true);
    if (business) {
      try {
        const data = await api.get(`/site-location/my-site`, {
          params: { limit: 100000, populate: "businesses", business },
        });
        setSiteList(data);
      } catch {
        setSiteList([]);
      }
    }
    setSiteLoading(false);
  };

  // get device
  const getDevice = async (site) => {
    if (!site?.length) return setDeviceList([]);
    setIsDeviceLoading(true);
    try {
      setDeviceList([]);
      const { results } = await api.get(endpoint.get_devices, {
        params: {
          site,
          limit: 100000,
          page: 1,
          hasTariff: 1,
          populate: "electricityTariff",
        },
      });

      if (results) {
        setDeviceList(results);
      }
    } catch {
      setDeviceList([]);
    } finally {
      setIsDeviceLoading(false);
    }
  };
  const getTariffInfo = async (params) => {
    setIsLoading(true);
    try {
      const result = await api.get("/device/tariff-data", {
        params,
      });
      // setTariffInfo(result);
      return result;
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
    setIsLoading(false);
    return {};
  };

  useEffect(() => {
    document.title = `${appConfig.name} - Electricity Usage Reports`;
    getBusiness();
  }, []);

  // handler
  const onFinish = async (values) => {
    const { site, device, month, type, business } = values;
    const selectedSite = siteList.find((s) => s._id === site) || {};
    const selectedDevice = deviceList.find((d) => d._id === device) || {};

    const tariffPricing = await getTariffInfo({
      device,
      month: month.format("MMM"),
      year: month.format("YYYY"),
      tax: type,
    });

    // value data
    const peakHourElectricityValue = tariffPricing?.electricityTariff?.peakHourValue || tariffPricing?.peakUsageKwh || 0;
    const offPeakHourElectricityValue = tariffPricing?.electricityTariff?.offPeakHourValue || tariffPricing?.offPeakUsageKwh || 0;
    const totalElectricityValue = tariffPricing?.totalUsageKwh ?? (tariffPricing?.electricityTariff?.totalValue || 0);
    const demandTotalValue = tariffPricing?.maximumDemandKw ?? (tariffPricing?.maximumDemandCost?.totalValue || 0);
    // price
    const peakHourElectricityPrice = tariffPricing?.electricityTariff?.peakHourPrice || 0;
    const offPeakHourElectricityPrice = tariffPricing?.electricityTariff?.offPeakHourPrice || 0;
    const totalElectricityPrice = tariffPricing?.electricityTariff?.totalPrice || tariffPricing?.energyCharge || 0;
    const demandTotalPrice = tariffPricing?.maximumDemandCost?.totalPrice || (tariffPricing?.capacityCharge + tariffPricing?.networkCharge) || 0;
    const serviceTax = tariffPricing?.serviceTax ?? (((Number(peakHourElectricityPrice) + Number(demandTotalPrice)) * type) / 100 || 0);
    const totalCharge = tariffPricing?.totalCharge ?? (Number(totalElectricityPrice) + Number(demandTotalPrice) + Number(serviceTax) || 0);

    const firstBusiness = businessList.find((b) => b._id === business) || businessList[0];

    setReportValues((pre) => ({
      ...pre,
      reportGenerationDate: moment().format("HH:mm:ss | DD/MM/YYYY") || "",
      monthlyCycleSelected: month ? formatDateRangeForMonth(month) : "",
      site: selectedSite?.name || "",
      siteAddress1: selectedSite?.buildingAddress1 || "",
      siteAddress2: selectedSite?.buildingAddress2 || "",
      // device: selectedDevice?.serial || "",
      device: selectedDevice?.name || "",
      installedArea: selectedDevice?.installedArea || "",
      business: selectedSite?.businesses?.name || "",
      type,
      tariffName: selectedDevice?.electricityTariff?.name || tariffPricing?.tariffName || "",
      reportType: getReportType(selectedDevice?.electricityTariff?.name || tariffPricing?.tariffName),
      totalUsageKwh: tariffPricing?.totalUsageKwh ?? totalElectricityValue,
      maximumDemandKw: tariffPricing?.maximumDemandKw ?? demandTotalValue,
      energyCharge: tariffPricing?.energyCharge ?? totalElectricityPrice,
      capacityCharge: tariffPricing?.capacityCharge ?? 0,
      networkCharge: tariffPricing?.networkCharge ?? 0,
      retailCharge: tariffPricing?.retailCharge ?? 0,
      afaCharge: tariffPricing?.afaCharge ?? 0,
      incentiveCharge: tariffPricing?.incentiveCharge ?? 0,
      peakHourElectricityPrice,
      offPeakHourElectricityPrice,
      totalElectricityPrice,
      demandTotalPrice,
      serviceTax,
      totalCharge,
      peakHourElectricityValue,
      offPeakHourElectricityValue,
      totalElectricityValue,
      demandTotalValue,
      firstBusiness,
    }));

    setIsLoading(false);
  };
  const onFinishFailed = (errorInfo) => {
    // eslint-disable-next-line no-console
    console.log("Failed:", errorInfo);
  };

  const isDisabled = !formData?.site || !formData?.device || !formData?.month;

  return (
    <Spin spinning={isLoading}>
      <div className="analysis">
        <div className="card p-3 m-md-3">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <label className="az-content-label mb-0">Manage Analysis and Reporting</label>
              <span className="d-block py-2">Electricity Usage Reports </span>
            </div>
          </div>
          <hr className="" />

          <div>
            <Form
              name="users"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              form={form}
              onValuesChange={(_, values) => {
                setFormData(values);
              }}
              initialValues={{}}
            >
              <Row lg={6} md={3} sm={2} xs={1} className="g-3">
                <Col>
                  <Form.Item name="business" className="w-100">
                    <Select
                      showSearch={true}
                      placeholder="All Business"
                      loading={businessLoading}
                      allowClear
                      disabled={businessList.length === 1}
                      onClear={() => {
                        setDeviceList([]);
                        setSiteList([]);
                      }}
                      onChange={(values) => {
                        getSiteLocations(values);
                        form.setFieldValue("site", undefined);
                        form.setFieldValue("device", undefined);
                        setFormData((pre) => ({ ...pre, site: undefined, device: undefined }));
                      }}
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      options={[
                        ...businessList.map((item) => ({
                          label: `${item.name} ${item?.id ? "(" + item.id + ")" : ""} `,
                          value: item._id,
                        })),
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item name="site" className="w-100">
                    <Select
                      showSearch={true}
                      placeholder="All Site Locations"
                      loading={siteLoading}
                      allowClear
                      onClear={() => {
                        setDeviceList([]);
                      }}
                      onChange={(values) => {
                        getDevice(values);
                        form.setFieldValue("device", undefined);
                        setFormData((pre) => ({ ...pre, device: undefined }));
                      }}
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      options={[
                        ...siteList.map((item) => ({
                          label: `${item.name} ${item?.id ? "(" + item.id + ")" : ""} `,
                          value: item._id,
                        })),
                      ]}
                    />
                  </Form.Item>
                </Col>

                <Col>
                  <Form.Item name="device">
                    <Select
                      style={{ width: "100%" }}
                      showSearch
                      allowClear
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      maxTagCount="responsive"
                      placeholder="Select Device"
                      loading={isDeviceLoading}
                      options={
                        Array.isArray(deviceList)
                          ? deviceList?.map((item) => ({ label: item.name, value: item._id }))
                          : []
                      }
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item name="month">
                    <DatePicker
                      picker="month"
                      format={"MMM, YYYY"}
                      allowClear
                      style={{ width: "100%" }}
                      disabledDate={disabledDate}
                    />
                  </Form.Item>
                </Col>

                <Col>
                  <Form.Item name="type" className="w-100">
                    <Select
                      showSearch={true}
                      placeholder="Select Type"
                      loading={siteLoading}
                      allowClear
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      options={[
                        { label: "With Service Tax (8%)", value: 8 },
                        { label: "Without Service Tax (0%)", value: 0 },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Button htmlType="submit" type="primary" disabled={isDisabled}>
                    Generate
                  </Button>
                </Col>
              </Row>

              {/* body */}
              <BillingReportBody {...{ reportValues }} />

              <div className="d-flex align-items-center justify-content-end gap-2 pt-2">
                <PDFDownloadLink
                  document={<BillingReportPDF {...{ reportValues }} />}
                  fileName={`Electricity Report${reportValues?.site ? " - " + reportValues?.site : ""} ${
                    formData?.month?.isValid() ? " - " + formData?.month?.format("MMM YYYY") : ""
                  } - ${moment().format("DD.M.YY")}.pdf`}
                >
                  {({ loading }) => (
                    <Button disabled={isDisabled} type="primary" loading={loading || isLoading}>
                      Download PDF
                    </Button>
                  )}
                </PDFDownloadLink>

                <Button type="primary" className="bg-secondary" onClick={() => navigate("/data-analysis")}>
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default BillingReport;

// is disable or not
const disabledDate = (current) => {
  // return false
  let date = new Date();
  const currentDate = moment(date).format("MMM, YYYY");
  const momentDate = current.format("MMM, YYYY");
  const dayOfCurrentMonth = date.getDate();
  const dayFlag = dayOfCurrentMonth < 7 && currentDate === momentDate;
  const dateCurrent = Number(moment(date).format("x"));
  return (current && current.valueOf() > dateCurrent) || dayFlag;
};
